/*
 * Meeting-transcript API client (happy-services-app).
 *
 * Thin REST wrapper over the backend Meeting Transcript module
 * (HappyPOS-backend `src/api/controllers/meetingTranscript.controller.js`).
 * JSON calls reuse the shared `apiFetch` (auth + GET dedup); the audio uploads
 * are hand-rolled POSTs because the `/audio` routes are mounted outside the
 * swagger router (see the backend `preSwaggerRoutes` mount). This app has no
 * swagger client — paths are called directly.
 *
 * TRANSPORT: audio goes up as base64 inside an `application/json` body, NOT as
 * `multipart/form-data`. The API sits behind a Cloudflare Worker load balancer
 * that does not carry multipart bodies reliably — a <2 MB multipart upload,
 * fully transmitted in 171 ms, still came back `origin_bad_gateway` at 618 ms,
 * while JSON POSTs to sibling meeting routes on the same host succeed. The
 * failure follows the content type, not the payload size. Base64 costs ~33%
 * more bytes; that is the accepted price for a transport that arrives.
 */

import * as FileSystem from 'expo-file-system';
import { apiFetch } from '../helpers/WorkSheetContext';
import { getAccessToken } from '../helpers/storage';

const API_URL = 'https://api.happysolutions.hu/api';

// Above this a recording is uploaded in parts instead of one request. Matches the
// smallest part the backend accepts (5 MiB), so anything needing more than one
// part takes the chunked route and everything smaller keeps the single POST.
const CHUNKED_UPLOAD_MIN_BYTES = 5 * 1024 * 1024;

/** Build a query string from a plain object, skipping empty values. */
const qs = (params = {}) => {
    const pairs = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);

    return pairs.length ? `?${pairs.join('&')}` : '';
};

/**
 * Create a meeting record (status `recording`). Returns the created meeting incl.
 * its `_id`, which every subsequent call needs.
 * @param {Object} payload { title, platform, host, participants, startedAt,
 *   language, relatedLeadId, relatedCustomerId, consent }
 * @returns {Promise<Object>}
 */
export const createMeeting = (payload = {}) =>
    apiFetch('/meetings', {
        method: 'POST',
        body: JSON.stringify(payload),
    });

/**
 * List meetings. Pass `{ mine: true }` for the personal "Meeting protocols" view.
 * @param {Object} [params] { status, search, page, limit, mine, leadId, customerId }
 * @returns {Promise<{ items: Array, total: number, page: number, limit: number, pages: number }>}
 */
export const listMeetings = (params = {}) =>
    apiFetch(`/meetings${qs(params)}`);

/**
 * Fetch one meeting with its full transcript, speakers and summaries.
 * @param {string} id
 * @param {Object} [opts] { mine }
 * @returns {Promise<Object>}
 */
export const getMeeting = (id, opts = {}) =>
    apiFetch(`/meetings/${id}${qs(opts)}`, { skipDedup: true });

/**
 * Update meeting metadata (additive: title, platform, host, participants, timing,
 * language, status).
 * @param {string} id
 * @param {Object} patch
 * @returns {Promise<Object>}
 */
export const updateMeeting = (id, patch = {}) =>
    apiFetch(`/meetings/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
    });

/** Delete a meeting (also purges its stored audio). */
export const deleteMeeting = (id) =>
    apiFetch(`/meetings/${id}`, { method: 'DELETE' });

/**
 * Upload one recorded audio segment (base64 JSON). For long recordings this is
 * called once per ~5-min chunk with an incrementing `segmentIndex` and the
 * chunk's absolute `startOffsetSec`; a single-shot upload leaves both at 0.
 *
 * @param {string} id Meeting id.
 * @param {Object} args
 * @param {string} args.uri Local file URI of the recorded chunk.
 * @param {'mic'|'meeting'} [args.kind='meeting'] Lane. `meeting` is diarised; `mic` is forced to "You".
 * @param {number} [args.segmentIndex=0] 0-based order within the lane.
 * @param {number} [args.startOffsetSec=0] Seconds from the meeting start.
 * @param {number} [args.durationSec=0] Chunk duration in seconds.
 * @param {string} [args.mimeType] e.g. 'audio/m4a', 'audio/mp4', 'audio/webm'.
 * @param {string} [args.fileName]
 * @returns {Promise<{ id: string, status: string, tracks: number }>}
 */
export const uploadMeetingSegment = async (id, {
    uri,
    kind = 'meeting',
    segmentIndex = 0,
    startOffsetSec = 0,
    durationSec = 0,
    mimeType,
    fileName,
} = {}) => {
    if (!uri) {
        throw new Error('No audio file to upload');
    }

    // A long recording cannot go up in one request: the body crosses the API
    // hostname's Cloudflare zone, the Worker load balancer and the origin
    // hostname's own Cloudflare zone, and gets reset somewhere in that chain
    // (the client sees `origin_bad_gateway`). Past the part threshold the file is
    // sent as a series of small parts instead; anything smaller keeps the cheaper
    // single request.
    const size = await fileSize(uri);

    if (size > CHUNKED_UPLOAD_MIN_BYTES) {
        return uploadMeetingSegmentChunked(id, {
            uri,
            kind,
            segmentIndex,
            startOffsetSec,
            durationSec,
            mimeType,
            fileName,
            size,
        });
    }

    return uploadMeetingSegmentSingle(id, {
        uri,
        kind,
        segmentIndex,
        startOffsetSec,
        durationSec,
        mimeType,
        fileName,
    });
};

/**
 * Single-request upload — the original path, used for small segments.
 * @param {string} id
 * @param {Object} args Same shape as `uploadMeetingSegment`.
 * @returns {Promise<{ id: string, status: string, tracks: number }>}
 */
const uploadMeetingSegmentSingle = async (id, {
    uri,
    kind = 'meeting',
    segmentIndex = 0,
    startOffsetSec = 0,
    durationSec = 0,
    mimeType,
    fileName,
} = {}) => {
    const token = await getAccessToken();

    if (!token) {
        throw new Error('No auth token');
    }
    if (!uri) {
        throw new Error('No audio file to upload');
    }

    const name = fileName || uri.split('/').pop() || `${kind}-${segmentIndex}.m4a`;
    const type = mimeType || guessMimeFromName(name);
    // Read the whole file as base64. Only reached below the chunk threshold, so
    // the string stays small enough to hold in memory; anything larger takes the
    // ranged, part-by-part path instead.
    const audio = await FileSystem.readAsStringAsync(uri, {
        encoding: FileSystem.EncodingType.Base64,
    });

    if (!audio) {
        throw new Error('Recorded file is empty');
    }

    const res = await fetch(`${API_URL}/meetings/${id}/audio`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        // The backend accepts audio | file | files | attachment; use `audio`.
        // Raw base64, no `data:` prefix — the server takes either.
        body: JSON.stringify({
            audio,
            fileName: name,
            mimeType: type,
            kind,
            segmentIndex,
            startOffsetSec,
            durationSec,
        }),
    });

    if (!res.ok) {
        const body = await res.text().catch(() => '');

        throw new Error(`Audio upload failed (${res.status}): ${body}`);
    }

    return res.json();
};
/*
 * ---------------------------------------------------------------------------
 * Chunked upload — begin -> part * N -> complete
 * ---------------------------------------------------------------------------
 * Mirrors the admin recorder. The backend opens an S3 multipart upload and
 * hands back the part size it wants, so the split can be retuned server-side
 * without shipping a new app build.
 *
 * React Native cannot slice a file the way a browser slices a Blob, so each part
 * is read as a base64 byte RANGE via `readAsStringAsync({ position, length })`
 * and posted as JSON. Only one part is ever held in memory, so usage stays
 * bounded no matter how long the recording is.
 */

/** Read the size of a local file, 0 when it cannot be determined. */
const fileSize = async (uri) => {
    try {
        const info = await FileSystem.getInfoAsync(uri, { size: true });

        return (info && info.exists && Number(info.size)) || 0;
    } catch (_e) {
        // An unreadable file falls through to the single-request path, which
        // reports the real failure with the server's own message.
        return 0;
    }
};

/** POST a JSON body to an audio-upload control endpoint. */
const postJson = async (path, payload) => {
    const token = await getAccessToken();

    if (!token) {
        throw new Error('No auth token');
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload || {}),
    });

    if (!res.ok) {
        const body = await res.text().catch(() => '');

        throw new Error(`Audio upload failed (${res.status}): ${body}`);
    }

    return res.json();
};

/**
 * Upload one part: read the byte range as base64 and POST it as JSON.
 *
 * The base64 string is sent straight through, so no temp file is staged on disk
 * — the ranged read already bounds memory to one part regardless of how long the
 * recording is.
 *
 * @param {Object} args
 * @returns {Promise<{ partNumber: number, etag: string }>}
 */
const uploadOnePart = async ({
    id, uri, uploadId, objectKey, partNumber, position, length, token,
}) => {
    const chunk = await FileSystem.readAsStringAsync(uri, {
        encoding: FileSystem.EncodingType.Base64,
        position,
        length,
    });

    const res = await fetch(`${API_URL}/meetings/${id}/audio/chunk`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            chunk,
            uploadId,
            objectKey,
            partNumber,
        }),
    });

    if (!res.ok) {
        const body = await res.text().catch(() => '');

        throw new Error(`Chunk ${partNumber} failed (${res.status}): ${body}`);
    }
    const stored = await res.json();

    return {
        partNumber,
        etag: stored.etag,
    };
};

/**
 * Upload a recorded segment as a sequence of parts. Falls back to nothing — a
 * failure aborts the multipart upload so no orphaned parts are left in the
 * bucket, then rethrows for the caller's own retry.
 *
 * @param {string} id Meeting id.
 * @param {Object} args
 * @param {string} args.uri Local file URI of the recorded chunk.
 * @param {'mic'|'meeting'} [args.kind='meeting']
 * @param {number} [args.segmentIndex=0]
 * @param {number} [args.startOffsetSec=0]
 * @param {number} [args.durationSec=0]
 * @param {string} [args.mimeType]
 * @param {string} [args.fileName]
 * @param {number} [args.size] Known byte length, re-read when absent.
 * @returns {Promise<{ id: string, status: string, tracks: number }>}
 */
export const uploadMeetingSegmentChunked = async (id, {
    uri,
    kind = 'meeting',
    segmentIndex = 0,
    startOffsetSec = 0,
    durationSec = 0,
    mimeType,
    fileName,
    size,
} = {}) => {
    const token = await getAccessToken();

    if (!token) {
        throw new Error('No auth token');
    }
    const name = fileName || uri.split('/').pop() || `${kind}-${segmentIndex}.m4a`;
    const type = mimeType || guessMimeFromName(name);
    const total = size || await fileSize(uri);

    if (!total) {
        throw new Error('Recorded file is empty');
    }

    const opened = await postJson(`/meetings/${id}/audio/upload`, {
        kind,
        segmentIndex,
        mimeType: type,
    });
    // Never go below the provider minimum: every part but the last must clear it.
    const partSize = Math.max(
        Number(opened.minPartBytes) || 0,
        Number(opened.partSizeBytes) || 0,
    ) || CHUNKED_UPLOAD_MIN_BYTES;
    const parts = [];

    try {
        for (let position = 0, partNumber = 1; position < total; position += partSize, partNumber += 1) {
            // Sequential on purpose: parallel parts multiply the chance of hitting
            // the same flaky hop, and every ETag is needed before completion.
            // eslint-disable-next-line no-await-in-loop
            const stored = await uploadOnePart({
                id,
                uri,
                uploadId: opened.uploadId,
                objectKey: opened.objectKey,
                partNumber,
                position,
                length: Math.min(partSize, total - position),
                token,
            });

            parts.push(stored);
        }

        return await postJson(`/meetings/${id}/audio/upload/complete`, {
            uploadId: opened.uploadId,
            objectKey: opened.objectKey,
            parts,
            kind,
            segmentIndex,
            startOffsetSec,
            durationSec,
            mimeType: type,
            size: total,
        });
    } catch (e) {
        // Best-effort cleanup: the upload has already failed, so a failing abort
        // must not replace the error the caller needs to see.
        postJson(`/meetings/${id}/audio/upload/abort`, {
            uploadId: opened.uploadId,
            objectKey: opened.objectKey,
        }).catch(() => null);
        throw e;
    }
};


/*
 * ---------------------------------------------------------------------------
 * Download a finished recording
 * ---------------------------------------------------------------------------
 * The API returns short-lived presigned URLs rather than the bytes, so audio is
 * pulled straight from object storage instead of back through the proxy chain
 * the upload path had to be redesigned to avoid. A segmented recording yields
 * one entry per part, ordered for playback; parts are NOT concatenated
 * server-side.
 */

/**
 * List presigned download links for a meeting's recording.
 *
 * URLs expire (see `expiresIn` / `expiresAt` on the response) — fetch them when
 * the user acts, not when the screen mounts, and re-fetch after a failure.
 *
 * @param {string} id Meeting id.
 * @param {Object} [opts]
 * @param {'mic'|'meeting'} [opts.kind] Restrict to one lane; omit for all.
 * @returns {Promise<{ id: string, status: string, expiresIn: number,
 *   expiresAt: string, files: Array<Object> }>}
 */
export const getMeetingRecording = (id, { kind } = {}) =>
    apiFetch(`/meetings/${id}/audio${qs({ kind })}`, { skipDedup: true });

/**
 * Download a recording to the app's document directory.
 *
 * Streams to disk via `downloadAsync` rather than reading the body into memory —
 * a long recording must never be materialised as a string on a phone. Returns
 * one local file per segment, in playback order.
 *
 * @param {string} id Meeting id.
 * @param {Object} [opts]
 * @param {'mic'|'meeting'} [opts.kind] Restrict to one lane; omit for all.
 * @param {(done: number, total: number) => void} [opts.onProgress] Per-file tick.
 * @returns {Promise<Array<{ uri: string, fileName: string, kind: string,
 *   segmentIndex: number, startOffsetSec: number, durationSec: number,
 *   mimeType: string, size: number }>>}
 */
export const downloadMeetingRecording = async (id, { kind, onProgress } = {}) => {
    const recording = await getMeetingRecording(id, { kind });
    const files = (recording && recording.files) || [];

    if (!files.length) {
        throw new Error('This meeting has no recording to download.');
    }

    const saved = [];

    for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        // Namespaced by meeting id so two meetings cannot overwrite each other,
        // and stable per segment so a re-download replaces rather than piles up.
        const target = `${FileSystem.documentDirectory}meeting-${id}-${file.fileName}`;

        // eslint-disable-next-line no-await-in-loop
        const { status, uri } = await FileSystem.downloadAsync(file.url, target);

        if (status !== 200) {
            throw new Error(
                `Recording download failed (${status}). The link may have expired — try again.`,
            );
        }

        saved.push({
            uri,
            fileName: file.fileName,
            kind: file.kind,
            segmentIndex: file.segmentIndex,
            startOffsetSec: file.startOffsetSec,
            durationSec: file.durationSec,
            mimeType: file.mimeType,
            size: file.size,
        });

        if (typeof onProgress === 'function') {
            onProgress(i + 1, files.length);
        }
    }

    return saved;
};

/**
 * Enqueue transcription + summary generation. Returns 202 immediately; poll
 * `getMeeting` until status is `ready` (or `failed`).
 * @param {string} id
 * @param {Object} [opts] { locale }
 * @returns {Promise<Object>}
 */
export const processMeeting = (id, opts = {}) =>
    apiFetch(`/meetings/${id}/process`, {
        method: 'POST',
        body: JSON.stringify(opts),
    });

/** Rename a diarised speaker on a transcript. */
export const renameSpeaker = (id, speakerKey, resolvedName) =>
    apiFetch(`/meetings/${id}/speakers/rename`, {
        method: 'POST',
        body: JSON.stringify({
            speakerKey,
            resolvedName,
        }),
    });

/** Generate an additional summary style (costs AI credits). */
export const generateSummary = (id, { style, locale, customPrompt } = {}) =>
    apiFetch(`/meetings/${id}/summaries`, {
        method: 'POST',
        body: JSON.stringify({
            style,
            locale,
            customPrompt,
        }),
    });

/** Email a summary to recipients. */
export const sendSummaryEmail = (id, { style, locale, subject, recipients } = {}) =>
    apiFetch(`/meetings/${id}/send`, {
        method: 'POST',
        body: JSON.stringify({
            style,
            locale,
            subject,
            recipients,
        }),
    });

/** Fetch per-tenant module settings (merged with defaults, incl. the `mobile` block). */
export const getMeetingSettings = () =>
    apiFetch('/meetings/settings', { skipDedup: true });

/** Upsert per-tenant module settings. Send the whole `mobile` sub-object to change it. */
export const updateMeetingSettings = (update = {}) =>
    apiFetch('/meetings/settings', {
        method: 'PATCH',
        body: JSON.stringify(update),
    });

/**
 * List the saved S3 / R2 storage connections (masked) so the settings screen can
 * offer them as the "bucket" for meeting-audio storage. The backend endpoint is
 * `system:admin`-scoped, so a non-admin operator gets 403 — we swallow it and
 * return an empty list; the UI then falls back to the default (env) bucket and
 * only exposes the object-key prefix.
 * @returns {Promise<Array<{ _id: string, name: string, provider: string, bucket: string, isDefault: boolean }>>}
 */
export const listStorageConnections = async () => {
    try {
        const res = await apiFetch('/storage-connections', { skipDedup: true });

        return Array.isArray(res?.connections) ? res.connections : [];
    } catch (_e) {
        return [];
    }
};

/** Best-effort MIME guess from a recorded file name/extension. */
function guessMimeFromName(name = '') {
    const ext = String(name).split('.').pop().toLowerCase();

    switch (ext) {
        case 'm4a':
        case 'mp4':
            return 'audio/mp4';
        case 'aac':
            return 'audio/aac';
        case 'caf':
            return 'audio/x-caf';
        case 'wav':
            return 'audio/wav';
        case 'mp3':
            return 'audio/mpeg';
        case 'ogg':
        case 'oga':
            return 'audio/ogg';
        case 'webm':
            return 'audio/webm';
        default:
            return 'audio/mp4';
    }
}
